import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingDown,
  Warning,
  ErrorOutline,
  Speed,
  Psychology,
  Timeline,
  ArrowForward,
  InfoOutlined,
} from '@mui/icons-material';
import { metricsService } from '../../services/metricsService';

interface PageIssue {
  pageId: number;
  pageUrl: string;
  issueType: 'anomaly' | 'momentum' | 'oci' | 'freshness';
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  impact: string;
  lastChecked: string;
}

export const AttentionDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');

  // Fetch sites
  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => metricsService.getSites(),
  });

  const selectedSiteId = sites?.[0]?.id;

  // Mock data - will be replaced with real API
  const mockIssues: PageIssue[] = [
    {
      pageId: 1,
      pageUrl: 'https://zendigitalworld.com/greetings/',
      issueType: 'anomaly',
      severity: 'critical',
      title: 'Traffic Drop Detected',
      description: '45% decrease in clicks over the last 7 days',
      impact: 'High - This is a top performing page',
      lastChecked: new Date().toISOString(),
    },
    {
      pageId: 2,
      pageUrl: 'https://zendigitalworld.com/morning-messages/',
      issueType: 'momentum',
      severity: 'warning',
      title: 'Declining Momentum',
      description: 'Position dropping steadily (-3 positions in 14 days)',
      impact: 'Medium - Close to losing top 10 ranking',
      lastChecked: new Date().toISOString(),
    },
    {
      pageId: 3,
      pageUrl: 'https://zendigitalworld.com/birthday-greetings/',
      issueType: 'oci',
      severity: 'warning',
      title: 'Low Competitive Strength',
      description: 'OCI Score: 28/100 - Below average competitiveness',
      impact: 'Medium - Vulnerable to competitors',
      lastChecked: new Date().toISOString(),
    },
  ];

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'anomaly': return <TrendingDown />;
      case 'momentum': return <Speed />;
      case 'oci': return <Psychology />;
      default: return <Warning />;
    }
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' => {
    switch (severity) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  const filteredIssues = selectedSeverity === 'all' 
    ? mockIssues 
    : mockIssues.filter(i => i.severity === selectedSeverity);

  const criticalCount = mockIssues.filter(i => i.severity === 'critical').length;
  const warningCount = mockIssues.filter(i => i.severity === 'warning').length;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" flexDirection="column" gap={3}>
        {/* Header */}
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            🚨 Attention Needed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Pages requiring immediate attention based on performance issues and trends
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3">{criticalCount}</Typography>
                    <Typography variant="body1">Critical Issues</Typography>
                  </Box>
                  <ErrorOutline sx={{ fontSize: 48, opacity: 0.7 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3">{warningCount}</Typography>
                    <Typography variant="body1">Warnings</Typography>
                  </Box>
                  <Warning sx={{ fontSize: 48, opacity: 0.7 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardActionArea onClick={() => navigate('/pages')}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h3">All Pages</Typography>
                      <Typography variant="body1">View full page list</Typography>
                    </Box>
                    <ArrowForward sx={{ fontSize: 48, opacity: 0.5 }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        {/* Filter by Severity */}
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              Filter:
            </Typography>
            <Chip
              label="All"
              color={selectedSeverity === 'all' ? 'primary' : 'default'}
              onClick={() => setSelectedSeverity('all')}
              variant={selectedSeverity === 'all' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Critical"
              color={selectedSeverity === 'critical' ? 'error' : 'default'}
              onClick={() => setSelectedSeverity('critical')}
              variant={selectedSeverity === 'critical' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Warning"
              color={selectedSeverity === 'warning' ? 'warning' : 'default'}
              onClick={() => setSelectedSeverity('warning')}
              variant={selectedSeverity === 'warning' ? 'filled' : 'outlined'}
            />
          </Stack>
        </Paper>

        {/* Issues List */}
        <Stack spacing={2}>
          {filteredIssues.length > 0 ? (
            filteredIssues.map((issue) => (
              <Card key={issue.pageId} variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            icon={getIssueIcon(issue.issueType)}
                            label={issue.severity.toUpperCase()}
                            color={getSeverityColor(issue.severity)}
                            size="small"
                          />
                          <Typography variant="h6">{issue.title}</Typography>
                        </Stack>
                        
                        <Typography 
                          variant="body2" 
                          color="primary.main"
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => navigate(`/pages/${issue.pageId}/changes`)}
                        >
                          {issue.pageUrl}
                        </Typography>

                        <Typography variant="body1">
                          {issue.description}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <InfoOutlined fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {issue.impact}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Stack spacing={1} alignItems="flex-end">
                        <Tooltip title="View detailed change history">
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/pages/${issue.pageId}/changes`)}
                          >
                            <Timeline />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="View OCI analysis">
                          <IconButton
                            color="secondary"
                            onClick={() => navigate(`/pages/${issue.pageId}/oci`)}
                          >
                            <Psychology />
                          </IconButton>
                        </Tooltip>

                        <Typography variant="caption" color="text.secondary">
                          Last checked: {new Date(issue.lastChecked).toLocaleTimeString()}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          ) : (
            <Alert severity="success" icon={<InfoOutlined />}>
              <Typography variant="body1">
                No {selectedSeverity === 'all' ? '' : selectedSeverity} issues found. All pages are performing well!
              </Typography>
            </Alert>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default AttentionDashboard;
